import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Avatar from "../Avatar";
import { MaterialNames } from "../material_symbol_names";
import classNames from "classnames";
import Tooltip from "@/components/Tooltip";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";

import Icon from "@/components/Icon";
import Button from "@/components/Button";
import CopyButton from "@/components/CopyButton";
import { isInternalIdCopyField } from "@/lib/copyAriaLabel";
import { ActionCluster, type Action } from "@/components/MenuButton";
import { EditableAvatar } from "../Avatar/Avatar";
import Dialog from "@/components/Dialog";
import { Link } from "react-router-dom";
import type { Path } from "@/router";
import DetailsCard from "@/components/DetailsCard/DetailsCard";
import { CRMNote, NotePriorityValues } from "@/types";
import { toFormat } from "@/lib/dateHelpers";
import { formatNotePriority } from "@/features/global/components/NotesTable/noteHelpers";
import PushpinIcon from "@/assets/images/PushpinIcon";

const DEFAULT_METADATA_LINK_CLASS_NAME = "text-sys-brand-primary hover:underline";
const DEFAULT_TITLE_LINK_CLASS_NAME = "block text-sys-brand-primary hover:underline";
const IDENTITY_METADATA_LINK_CLASS_NAME =
  "text-inherit underline cursor-pointer hover:text-inherit focus-visible:text-inherit underline-offset-2";
const IDENTITY_TITLE_LINK_CLASS_NAME =
  "block text-inherit no-underline cursor-pointer hover:text-sys-brand-primary hover:underline focus-visible:text-sys-brand-primary focus-visible:underline underline-offset-4";

function metadataLinkClassName(identityLinkChrome?: boolean) {
  return identityLinkChrome ? IDENTITY_METADATA_LINK_CLASS_NAME : DEFAULT_METADATA_LINK_CLASS_NAME;
}

function titleLinkClassName(identityLinkChrome?: boolean) {
  return identityLinkChrome ? IDENTITY_TITLE_LINK_CLASS_NAME : DEFAULT_TITLE_LINK_CLASS_NAME;
}

const MetadataTooltip = ({ supportingText, children }: { supportingText: string; children: React.ReactNode }) => (
  <Tooltip
    variant="plain"
    color="black"
    supportingText={supportingText}
    menuCorner="end-start"
    anchorCorner="start-start"
    compactHoverArea
  >
    {children}
  </Tooltip>
);

/**
 * Represents a single tag element (typically a Tag component)
 */
export type TagElement = React.ReactNode;

/**
 * Represents either a single tag or an array of tags
 */
export type Tags = TagElement | TagElement[];

const EDITABLE_METADATA_STYLE: React.CSSProperties = {
  textDecorationLine: "underline",
  textDecorationStyle: "dotted",
  textDecorationThickness: "2px",
  textUnderlineOffset: "4px",
  textDecorationColor: "rgba(0, 0, 0, 0.25)"
};

function EditableMetadataValue({
  children,
  onClick,
  className,
  testId,
  label,
  underlined = true
}: {
  children: React.ReactNode;
  onClick: () => void;
  className?: string;
  testId?: string;
  label?: string;
  underlined?: boolean;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-label={label ? `Edit ${label}, ${String(children)}` : undefined}
      className={classNames(
        "bg-transparent border-0 p-0 m-0 text-left cursor-pointer rounded-sm appearance-none font-inherit hover:text-sys-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sys-brand-primary",
        className
      )}
      style={underlined ? EDITABLE_METADATA_STYLE : undefined}
    >
      {children}
    </button>
  );
}

/**
 * Represents the metadata structure for the ObjectHeading component.
 * Can be either a simple string or an array of objects with text, optional label, optional boldValue, optional tooltip, optional icon, optional copy control, optional href, and optional onClick.
 */
export type MetaData =
  | string
  | {
      text: string;
      label?: string;
      boldValue?: boolean;
      tooltipText?: string;
      iconName?: MaterialNames;
      /** When true, shows a plain clipboard control that copies `text`. */
      copyable?: boolean;
      /** When set, renders the value as an in-app link. Wins over `onClick`. */
      href?: string;
      /** When set (and `href` is not), renders the value as an editable control. */
      onClick?: () => void;
      /** Optional test id on the value control when `onClick` is set. */
      testId?: string;
    }[];

export interface PinnedNotesConfig {
  notes: CRMNote[];
  onViewAll?: () => void;
  getNoteAction?: (note: CRMNote) => (() => void) | undefined;
}

export type ObjectHeadingAction = Action;

/**
 * Variant types for ObjectHeading component
 */
export type ObjectHeadingVariant = "complete" | "condensed" | "landing-page";

/**
 * Props for the ObjectHeading component
 */
interface PageHeaderProps {
  /** The main title text displayed in the header */
  title: string;
  /** When set, renders the title as an in-app link. Ignored when `onTitleChange` is provided. */
  titleHref?: string;
  /**
   * When true, `titleHref` and metadata `href` keep header color at rest and underline on hover or keyboard focus.
   * Default false keeps primary-colored links for Event and other existing callers.
   */
  identityLinkChrome?: boolean;
  /**
   * Where the status tag renders. `"metadata"` (default) leads the metadata row.
   * `"title"` places it at the end of the title.
   */
  tagPlacement?: "metadata" | "title";
  /**
   * When true, metadata items stack in a column under the title at every breakpoint.
   */
  stackedMetadata?: boolean;
  /**
   * When false, metadata `onClick` values stay un-underlined. Default true draws the
   * dotted underline used for inline-editable header fields.
   */
  editableUnderline?: boolean;
  /** Optional callback invoked with the new title when the user edits inline and blurs or presses Enter. When provided, the title becomes editable on hover/click. The editor fills the title column with extra padding so the name stays readable. */
  onTitleChange?: (newTitle: string) => void;
  /** Optional subtitle or ID text */
  subText?: string;
  /** Optional Material icon name for the avatar when no `imageUrl` or `logoUrl` is provided */
  iconName?: MaterialNames;
  /** Optional URL for the avatar image */
  imageUrl?: string;
  /**
   * Brand mark on a borderless disc using the Avatar placeholder fill
   * (`bg-sys-brand-secondary-container`, object-contain, not Avatar crop).
   * Wins over `imageUrl` and `iconName`. Use for section social wordmarks — do not
   * pass those through `imageUrl`.
   */
  logoUrl?: string;
  /**
   * Complete-variant identity alignment. Title-only headers center by default;
   * pass `"start"` to left-align the logo and title (section profile).
   */
  align?: "start" | "center";
  /**
   * Ordered actions for the header. The first eligible (non-`menuOnly`) action
   * in array order is the inline primary when consolidation applies. With one
   * or two actions, eligible items render inline; with three or more actions
   * (including `menuOnly`), the first eligible stays inline and the rest
   * collapse into a single MenuButton. `menuOnly` actions never render inline,
   * but they still count toward the three-action overflow threshold.
   */
  actions?: Action[];
  /** Override the overflow menu trigger label (defaults to `More`). */
  menuButtonLabel?: string;
  /** Optional children elements to be rendered */
  children?: React.ReactNode;
  /** Flag to indicate if this is a details view */
  isDetailsView?: boolean;
  /** Flag to indicate if this is a desktop details view */
  isDetailsViewDesktop?: boolean;
  /** Optional tag element(s) (like status indicators). Can be a single tag or array of tags */
  tag?: Tags;
  /** Optional metadata to be displayed */
  metaData?: MetaData;
  /** Optional callback for image edit click */
  onImageEditClick?: () => void;
  /** Optional avatar size */
  avatarSize?: "small" | "medium" | "large" | "xlarge";
  /** Variant of the ObjectHeading. Auto-detects based on screen size if not specified */
  variant?: ObjectHeadingVariant;
  /** Whether to show full metadata under the title in condensed variant. Default: false */
  showFullMetadataInCondensed?: boolean;
  /** Optional pinned/elevated notes to display as a button + dialog */
  pinnedNotes?: PinnedNotesConfig;
  /** Whether the header is currently in sticky state */
  isSticky?: boolean;
  /** Whether to initially open the notes dialog */
  initialNotesDialogOpen?: boolean;
}

/**
 * Text component used for rendering individual metadata items
 * @internal
 */
const Text = ({
  children,
  label,
  boldValue,
  isDetailsViewDesktop,
  iconName,
  copyable,
  copyText,
  href,
  onClick,
  testId,
  tooltipText,
  identityLinkChrome,
  editableUnderline,
  stackedMetadata
}: {
  /** The content to be displayed */
  children: React.ReactNode;
  /** Optional label to be displayed before the value */
  label?: string;
  /** Flag to make the value bold */
  boldValue?: boolean;
  /** Flag to adjust styling for desktop details view */
  isDetailsViewDesktop?: boolean;
  /** Optional Material icon name for mobile view */
  iconName?: MaterialNames;
  /** When true, shows a plain clipboard control beside the value */
  copyable?: boolean;
  /** Exact string the clipboard control should copy */
  copyText?: string;
  /** When set, renders the value as an in-app link */
  href?: string;
  /** When set (and `href` is not), renders the value as an editable control */
  onClick?: () => void;
  /** Optional test id on the value control when `onClick` is set */
  testId?: string;
  /** When set with `href`/`onClick` and `label`, the tooltip attaches to the label so the control is not nested in a button */
  tooltipText?: string;
  /** When true, linked values inherit header color instead of using primary */
  identityLinkChrome?: boolean;
  /** When false, `onClick` values omit the dotted underline */
  editableUnderline?: boolean;
  /** When true, labels share a fixed column so stacked rows align */
  stackedMetadata?: boolean;
}) => {
  const { isCompactLayout } = useIsCompactLayout();
  const valueRef = useRef<HTMLSpanElement>(null);

  const textContent = typeof children === "string" ? children : (valueRef.current?.textContent ?? "");
  const resolvedCopyText = copyText ?? textContent;
  const copyFieldLabel = label ?? "Value";
  const copyFieldId = isInternalIdCopyField(label) && resolvedCopyText ? resolvedCopyText : undefined;
  const valueClassName = boldValue ? "text-body-large-bold lg:text-title-medium-bold" : "text-body-large";
  const isInteractive = Boolean(href || onClick);
  const stackedLabelColumnClass = stackedMetadata ? "min-w-[7.5rem] shrink-0" : undefined;
  const labelClassName = classNames(
    "text-body-large-bold lg:text-title-medium-bold mr-1 select-none",
    stackedLabelColumnClass
  );
  const labelColumn = label ? (
    isInteractive && tooltipText ? (
      <MetadataTooltip supportingText={tooltipText}>
        <span className={labelClassName} data-testid="object-heading-label-column">
          {label}
        </span>
      </MetadataTooltip>
    ) : (
      <span className={labelClassName} data-testid="object-heading-label-column">
        {label}
      </span>
    )
  ) : stackedMetadata ? (
    <span
      className={classNames("mr-1", stackedLabelColumnClass)}
      data-testid="object-heading-label-column"
      aria-hidden
    />
  ) : null;

  return (
    <div className="flex items-center">
      {isCompactLayout && iconName && (
        <Icon name={iconName} size={24} className="mr-2 text-sys-brand-on-surface-variant" />
      )}
      <div
        className={classNames("text-sys-brand-on-surface flex items-center gap-1 flex-wrap", {
          "w-full": isDetailsViewDesktop || stackedMetadata
        })}
      >
        {labelColumn}
        <div className="flex items-center gap-2">
          {href ? (
            <Link to={href as Path} className={classNames(metadataLinkClassName(identityLinkChrome), valueClassName)}>
              {children}
            </Link>
          ) : onClick ? (
            <EditableMetadataValue
              onClick={onClick}
              className={valueClassName}
              testId={testId}
              label={label}
              underlined={editableUnderline}
            >
              {children}
            </EditableMetadataValue>
          ) : (
            <span ref={valueRef} className={valueClassName}>
              {children}
            </span>
          )}
          {copyable && !!resolvedCopyText && (
            <CopyButton
              className="z-10"
              textToCopy={resolvedCopyText}
              copyFieldLabel={copyFieldLabel}
              copyFieldId={copyFieldId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/** @internal */
function EditableTitle({
  title,
  onTitleChange,
  onEditingChange,
  className
}: {
  title: string;
  onTitleChange: (newTitle: string) => void;
  onEditingChange?: (editing: boolean) => void;
  className: string;
}) {
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);
  const titleTextareaRef = useRef<HTMLTextAreaElement>(null);
  const titleSavingRef = useRef(false);

  useEffect(() => {
    setDraftTitle(title);
  }, [title]);

  const beginEditing = () => {
    titleSavingRef.current = false;
    onEditingChange?.(true);
    setIsTitleFocused(true);
  };

  const endEditing = () => {
    onEditingChange?.(false);
    setIsTitleFocused(false);
  };

  useEffect(() => {
    if (isTitleFocused && titleTextareaRef.current) {
      const textarea = titleTextareaRef.current;
      textarea.focus();
      textarea.setSelectionRange(0, textarea.value.length);
    }
  }, [isTitleFocused]);

  const saveTitle = () => {
    if (titleSavingRef.current) return;
    titleSavingRef.current = true;
    endEditing();
    const normalizedTitle = draftTitle.trim();
    if (normalizedTitle && normalizedTitle !== title) {
      onTitleChange(normalizedTitle);
    } else {
      setDraftTitle(title);
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveTitle();
    } else if (e.key === "Escape") {
      endEditing();
      setDraftTitle(title);
    }
  };

  if (isTitleFocused) {
    return (
      <div className="grid w-full min-w-0" style={{ gridTemplateColumns: "1fr" }} data-testid="editable-title-editor">
        <span
          aria-hidden
          className={classNames(
            className,
            "invisible whitespace-pre-wrap break-words overflow-hidden px-3 py-4 border-2 min-h-[5rem]"
          )}
          style={{ gridArea: "1 / 1" }}
        >
          {draftTitle || "\u00A0"}
          {"\u00A0"}
        </span>
        <textarea
          ref={titleTextareaRef}
          aria-label="Edit title"
          data-testid="editable-title-input"
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          onBlur={saveTitle}
          onKeyDown={handleTitleKeyDown}
          rows={1}
          className={classNames(
            className,
            "w-full bg-sys-brand-surface outline-none border-2 border-sys-brand-primary rounded-md px-3 py-4 min-h-[5rem] min-w-0 resize-none overflow-hidden"
          )}
          style={{ gridArea: "1 / 1" }}
        />
      </div>
    );
  }

  return (
    <p
      data-testid="editable-title"
      role="button"
      tabIndex={0}
      className={classNames(
        className,
        "cursor-pointer rounded-md px-2 -mx-2 border-2 border-transparent",
        "hover:border-sys-brand-outline-variant hover:bg-sys-brand-surface-variant hover:bg-opacity-10",
        "hover:![text-decoration-line:none]",
        "transition-colors"
      )}
      style={{
        textDecorationLine: "underline",
        textDecorationStyle: "dotted",
        textDecorationThickness: "2px",
        textUnderlineOffset: "8px",
        textDecorationColor: "rgba(0, 0, 0, 0.25)"
      }}
      onClick={beginEditing}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          beginEditing();
        }
      }}
    >
      {title}
    </p>
  );
}

function HeadingTitleWithOptionalTag({
  title,
  titleHref,
  onTitleChange,
  onEditingChange,
  className,
  identityLinkChrome,
  tag,
  tagPlacement,
  isTitleEditing
}: {
  title: string;
  titleHref?: string;
  onTitleChange?: (newTitle: string) => void;
  onEditingChange?: (editing: boolean) => void;
  className: string;
  identityLinkChrome?: boolean;
  tag?: React.ReactNode;
  tagPlacement?: "metadata" | "title";
  isTitleEditing?: boolean;
}) {
  const titleNode = (
    <HeadingTitle
      title={title}
      titleHref={titleHref}
      onTitleChange={onTitleChange}
      onEditingChange={onEditingChange}
      className={className}
      identityLinkChrome={identityLinkChrome}
    />
  );

  if (tagPlacement === "title" && tag && !isTitleEditing) {
    return (
      <div className="flex items-center gap-2 min-w-0 flex-wrap w-fit max-w-full">
        {titleNode}
        <div className="flex-shrink-0 self-center">{tag}</div>
      </div>
    );
  }

  return <div className="flex items-center gap-4 min-w-0 w-full">{titleNode}</div>;
}

function HeadingTitle({
  title,
  titleHref,
  onTitleChange,
  onEditingChange,
  className,
  identityLinkChrome
}: {
  title: string;
  titleHref?: string;
  onTitleChange?: (newTitle: string) => void;
  onEditingChange?: (editing: boolean) => void;
  className: string;
  identityLinkChrome?: boolean;
}) {
  if (onTitleChange) {
    return (
      <EditableTitle
        title={title}
        onTitleChange={onTitleChange}
        onEditingChange={onEditingChange}
        className={className}
      />
    );
  }

  if (titleHref) {
    return (
      <Link to={titleHref as Path} className={classNames(className, titleLinkClassName(identityLinkChrome))}>
        {title}
      </Link>
    );
  }

  return <p className={className}>{title}</p>;
}

const LOGO_SIZE_CLASS = {
  small: "w-5 h-5",
  medium: "w-10 h-10",
  large: "w-14 h-14",
  xlarge: "w-16 h-16"
} as const;

function HeadingMedia({
  logoUrl,
  imageUrl,
  iconName,
  avatarSize,
  onImageEditClick,
  className
}: {
  logoUrl?: string;
  imageUrl?: string;
  iconName?: MaterialNames;
  avatarSize: "small" | "medium" | "large" | "xlarge";
  onImageEditClick?: () => void;
  className?: string;
}) {
  if (logoUrl) {
    return (
      <div
        data-testid="object-heading-logo-frame"
        className={classNames(
          "flex items-center justify-center overflow-hidden rounded-full bg-sys-brand-secondary-container",
          LOGO_SIZE_CLASS[avatarSize],
          className
        )}
      >
        <img src={logoUrl} alt="" data-testid="object-heading-logo" className="h-full w-full object-contain p-[3%]" />
      </div>
    );
  }

  if (onImageEditClick) {
    return (
      <EditableAvatar
        size={avatarSize}
        picture={imageUrl}
        iconName={iconName}
        className={className}
        onEditClick={onImageEditClick}
      />
    );
  }

  return <Avatar size={avatarSize} picture={imageUrl} {...(!imageUrl && { iconName })} className={className} />;
}

/**
 * Renders single or multiple tags with proper spacing
 * @internal
 */
function renderTags(tag: Tags) {
  if (!tag) return null;

  if (Array.isArray(tag)) {
    return (
      <div className="flex items-center gap-2">
        {tag.map((singleTag, index) => (
          <React.Fragment key={index}>{singleTag}</React.Fragment>
        ))}
      </div>
    );
  }

  return <div>{tag}</div>;
}

/**
 * MetaData component for rendering the metadata section
 * @internal
 */
function MetaData({
  metaData,
  isDetailsViewDesktop,
  leadingContent,
  identityLinkChrome,
  stackedMetadata,
  editableUnderline
}: {
  metaData?: MetaData;
  isDetailsViewDesktop?: boolean;
  leadingContent?: React.ReactNode;
  identityLinkChrome?: boolean;
  stackedMetadata?: boolean;
  editableUnderline?: boolean;
}) {
  if (!metaData && !leadingContent) return null;

  if (Array.isArray(metaData)) {
    return (
      <div
        data-testid="object-heading-metadata"
        className={classNames("flex", {
          "mt-3 flex-col gap-y-1": stackedMetadata,
          "mt-1": !stackedMetadata,
          "flex-wrap gap-y-1 lg:gap-y-0 gap-x-5 lg:mt-0": !stackedMetadata,
          "items-center": leadingContent && !stackedMetadata,
          "flex-col lg:flex-row": !leadingContent && !stackedMetadata,
          "lg:flex-col": isDetailsViewDesktop && !stackedMetadata
        })}
      >
        {leadingContent && <div className="flex h-6 items-center">{leadingContent}</div>}
        {metaData.map(({ text, label, boldValue, tooltipText, iconName, copyable, href, onClick, testId }, index) => {
          if (!text) return null;
          const textNode = (
            <Text
              isDetailsViewDesktop={isDetailsViewDesktop}
              label={label}
              boldValue={boldValue}
              iconName={iconName}
              copyable={copyable}
              copyText={text}
              href={href}
              onClick={onClick}
              testId={testId}
              tooltipText={tooltipText}
              identityLinkChrome={identityLinkChrome}
              editableUnderline={editableUnderline}
              stackedMetadata={stackedMetadata}
            >
              {text}
            </Text>
          );
          return tooltipText && !href && !onClick ? (
            <MetadataTooltip key={index} supportingText={tooltipText}>
              {textNode}
            </MetadataTooltip>
          ) : (
            <React.Fragment key={index}>{textNode}</React.Fragment>
          );
        })}
      </div>
    );
  }

  if (!leadingContent) {
    return <p className="text-title-medium">{metaData}</p>;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-1 lg:mt-0">
      <div className="flex h-6 items-center">{leadingContent}</div>
      {metaData && <p className="text-title-medium">{metaData}</p>}
    </div>
  );
}

/**
 * ObjectHeading Component
 *
 * A versatile heading component that supports three distinct variants:
 *
 * **Complete** (default on desktop/tablet):
 * - Full horizontal layout with all metadata displayed
 * - Action buttons arranged horizontally
 * - Ideal for main page headers and detail views
 *
 * **Condensed** (default on mobile, always used in side panels):
 * - Compact horizontal layout with truncated text
 * - Medium-sized avatar, essential info only
 * - Perfect for side panels and mobile views
 *
 * **Landing Page** (explicit usage):
 * - Centered vertical layout with large avatar
 * - Designed for mobile profile pages
 * - Full-width action buttons stacked vertically
 *
 * Features:
 * - Title with optional avatar/icon
 * - Metadata with optional labels, bold values, and icons
 * - Action buttons with intelligent overflow handling
 * - Extended menu actions (dropdown menu)
 * - Status tags
 * - Additional content via children
 *
 * @example
 * ```tsx
 * // Complete variant (auto-detected on desktop)
 * <ObjectHeading title="John Smith" subText="PGA ID: 12345" />
 *
 * // Condensed variant (auto-detected on mobile, or explicit for side panels)
 * <ObjectHeading title="John Smith" subText="PGA ID: 12345" variant="condensed" />
 *
 * // Condensed variant with full metadata (for job/event pages on mobile)
 * <ObjectHeading
 *   title="Head Golf Professional"
 *   variant="condensed"
 *   showFullMetadataInCondensed={true}
 *   metaData={[
 *     { text: "Minnesota Section", boldValue: true },
 *     { text: "Jacksonville Beach Golf Course", label: "Facility" },
 *     { text: "Active", label: "Status" }
 *   ]}
 * />
 *
 * // Landing Page variant (explicit usage for mobile profile pages)
 * <ObjectHeading
 *   title="John Smith"
 *   subText="PGA ID: 12345"
 *   variant="landing-page"
 *   actions={[
 *     { text: "Edit Profile", onClick: handleEdit },
 *     { text: "Message", onClick: handleMessage }
 *   ]}
 * />
 *
 * // With metadata and tags
 * <ObjectHeading
 *   title="John Smith"
 *   subText="PGA ID: 12345"
 *   metaData={[
 *     { text: "Member", label: "Status:", iconName: "badge" },
 *     { text: "California Section", label: "Location:", boldValue: true, iconName: "location_on" }
 *   ]}
 *   tag={<Tag tagType="success">Active</Tag>}
 * />
 * ```
 *
 * @see https://www.figma.com/design/HJmPQurloNvKFxyfqvSV9Y/PGA-Product-Design-System?node-id=2786-43187&m=dev
 */
export default function ObjectHeading({
  title,
  titleHref,
  identityLinkChrome = false,
  tagPlacement = "metadata",
  stackedMetadata = false,
  editableUnderline = true,
  onTitleChange,
  subText,
  iconName = "domain",
  imageUrl,
  logoUrl,
  align,
  isDetailsViewDesktop,
  actions,
  menuButtonLabel,
  children,
  metaData,
  tag,
  avatarSize = "large",
  variant,
  showFullMetadataInCondensed = false,
  onImageEditClick,
  pinnedNotes,
  isSticky,
  initialNotesDialogOpen = false
}: PageHeaderProps) {
  const { isCompactLayout } = useIsCompactLayout();
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(initialNotesDialogOpen);

  useEffect(() => {
    if (initialNotesDialogOpen) {
      setNotesDialogOpen(true);
    }
  }, [initialNotesDialogOpen]);

  // Determine the variant to use
  const effectiveVariant: ObjectHeadingVariant = variant || (isCompactLayout ? "condensed" : "complete");

  const metaDataArray = !isCompactLayout
    ? [
        { text: subText || "", tooltipText: undefined, copyable: !!subText },
        ...((typeof metaData === "string" ? [{ text: metaData, tooltipText: undefined }] : metaData) || []).map(
          (item) => (typeof item === "string" ? { text: item, tooltipText: undefined } : item)
        )
      ]
    : metaData;

  const hasActions = !!actions && actions.length > 0;
  const isSimpleHeader = !subText && !metaData && !hasActions && !children && !tag;
  const centerIdentity = isSimpleHeader && align !== "start";

  const hasPinnedNotes = pinnedNotes && pinnedNotes.notes.length > 0;
  const hideNotesButton = isCompactLayout && isSticky;
  const renderedTag = !isTitleEditing && tag ? renderTags(tag) : null;

  const renderPinnedNotesButton = () =>
    hasPinnedNotes && !hideNotesButton ? (
      <div className="mt-2">
        <Button variant="squaredSmall" svgIcon={<PushpinIcon />} onClick={() => setNotesDialogOpen(true)}>
          {pinnedNotes.notes.length} {pinnedNotes.notes.length === 1 ? "Note" : "Notes"}
        </Button>
      </div>
    ) : null;

  // Render Complete variant (desktop/tablet)
  const renderCompleteVariant = () => (
    <div className="w-full pt-8">
      <div
        className={classNames("flex flex-col md:flex-row items-start md:gap-x-4", {
          "md:items-center": !stackedMetadata,
          "justify-between": !isSimpleHeader,
          "justify-center": centerIdentity
        })}
      >
        <div
          data-testid="object-heading-identity"
          className={classNames("flex items-start min-w-0", {
            "justify-center w-full": centerIdentity,
            "flex-1": !centerIdentity
          })}
        >
          <HeadingMedia
            logoUrl={logoUrl}
            imageUrl={imageUrl}
            iconName={iconName}
            avatarSize={avatarSize}
            onImageEditClick={onImageEditClick}
            className={classNames("mr-3 flex-shrink-0", { "self-center": !stackedMetadata })}
          />
          <div
            data-testid="object-heading-title-column"
            className={classNames("flex flex-col min-w-0", {
              "self-center": !stackedMetadata,
              "flex-1": !centerIdentity
            })}
          >
            <HeadingTitleWithOptionalTag
              title={title}
              titleHref={titleHref}
              onTitleChange={onTitleChange}
              onEditingChange={setIsTitleEditing}
              className="text-headline-medium-bold font-serif"
              identityLinkChrome={identityLinkChrome}
              tag={renderedTag}
              tagPlacement={tagPlacement}
              isTitleEditing={isTitleEditing}
            />
            <MetaData
              metaData={metaDataArray}
              isDetailsViewDesktop={isDetailsViewDesktop}
              leadingContent={tagPlacement === "title" ? undefined : renderedTag}
              identityLinkChrome={identityLinkChrome}
              stackedMetadata={stackedMetadata}
              editableUnderline={editableUnderline}
            />
            {children && <div>{children}</div>}
            {renderPinnedNotesButton()}
          </div>
        </div>
        {hasActions && !isTitleEditing && (
          <div className="flex flex-row flex-wrap md:flex-nowrap gap-2 items-center mt-4 md:mt-0 md:flex-shrink-0">
            <ActionCluster actions={actions} menuButtonLabel={menuButtonLabel} />
          </div>
        )}
      </div>
    </div>
  );

  // Render Condensed variant (side panels, mobile)
  const renderCondensedVariant = () => (
    <div className="w-full pt-7">
      <div className="flex items-start gap-3 mb-4">
        <HeadingMedia
          logoUrl={logoUrl}
          imageUrl={imageUrl}
          iconName={iconName}
          avatarSize={avatarSize}
          onImageEditClick={onImageEditClick}
          className="flex-shrink-0"
        />
        <div data-testid="object-heading-title-column" className="flex-1 min-w-0">
          <HeadingTitleWithOptionalTag
            title={title}
            titleHref={titleHref}
            onTitleChange={onTitleChange}
            onEditingChange={setIsTitleEditing}
            className="text-headline-small-bold font-serif"
            identityLinkChrome={identityLinkChrome}
            tag={renderedTag}
            tagPlacement={tagPlacement}
            isTitleEditing={isTitleEditing}
          />
          <MetaData
            metaData={
              showFullMetadataInCondensed
                ? metaDataArray
                : Array.isArray(metaData) && metaData.length > 0
                  ? [metaData[0]]
                  : undefined
            }
            leadingContent={tagPlacement === "title" ? undefined : renderedTag}
            identityLinkChrome={identityLinkChrome}
            stackedMetadata={stackedMetadata}
            editableUnderline={editableUnderline}
          />
          {renderPinnedNotesButton()}
          {hasActions && !isTitleEditing && (
            <div className="flex flex-wrap gap-2 items-center mt-3">
              <ActionCluster actions={actions} menuButtonLabel={menuButtonLabel} />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render Landing Page variant (mobile profile pages)
  const renderLandingPageVariant = () => (
    <div className="w-full text-center pt-8">
      <div className="flex flex-col items-center">
        <HeadingMedia
          logoUrl={logoUrl}
          imageUrl={imageUrl}
          iconName={iconName}
          avatarSize="xlarge"
          onImageEditClick={onImageEditClick}
          className="mb-4"
        />
        <HeadingTitle
          title={title}
          titleHref={titleHref}
          onTitleChange={onTitleChange}
          onEditingChange={setIsTitleEditing}
          className="text-headline-small-bold font-serif mb-4"
          identityLinkChrome={identityLinkChrome}
        />
        {subText && <p className="text-title-medium text-sys-brand-on-surface-variant mb-2">{subText}</p>}
        {metaData && (
          <div className="flex flex-col items-start gap-1 mb-3 w-full text-left">
            {Array.isArray(metaData) ? (
              metaData.map(({ text, label, boldValue, copyable, href, onClick, testId, tooltipText }, index) => {
                if (!text) return null;
                const labelNode = label ? (
                  <span className="text-body-medium font-bold text-sys-brand-on-surface-variant">{label} </span>
                ) : null;
                const landingValueClassName = classNames(
                  "text-body-medium text-left",
                  boldValue ? "font-bold text-sys-brand-on-surface" : "text-sys-brand-on-surface-variant"
                );
                return (
                  <div key={index} className="flex items-center gap-1 w-full text-left">
                    {labelNode && (href || onClick) && tooltipText ? (
                      <MetadataTooltip supportingText={tooltipText}>{labelNode}</MetadataTooltip>
                    ) : (
                      labelNode
                    )}
                    {href ? (
                      <Link
                        to={href as Path}
                        className={classNames(
                          metadataLinkClassName(identityLinkChrome),
                          "text-body-medium text-left",
                          boldValue ? "font-bold" : ""
                        )}
                      >
                        {text}
                      </Link>
                    ) : onClick ? (
                      <EditableMetadataValue
                        onClick={onClick}
                        className={landingValueClassName}
                        testId={testId}
                        label={label}
                        underlined={editableUnderline}
                      >
                        {text}
                      </EditableMetadataValue>
                    ) : (
                      <p className={landingValueClassName}>{text}</p>
                    )}
                    {copyable && (
                      <CopyButton
                        textToCopy={text}
                        copyFieldLabel={label ?? "Value"}
                        copyFieldId={isInternalIdCopyField(label) ? text : undefined}
                        className="flex-shrink-0"
                      />
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-title-medium text-left">{metaData}</p>
            )}
          </div>
        )}
        {!isTitleEditing && tag && <div className="mb-4">{renderTags(tag)}</div>}
        {children && <div className="mb-4">{children}</div>}
      </div>
    </div>
  );

  const renderNotesDialog = () => {
    if (!hasPinnedNotes) return null;

    return (
      <Dialog
        headline={`Pinned Notes (${pinnedNotes.notes.length})`}
        open={notesDialogOpen}
        onClosed={() => setNotesDialogOpen(false)}
        className="max-h-[85vh] w-[640px]"
        hasFormElements
        actions={
          <div className="flex w-full justify-between items-center">
            <div>
              {pinnedNotes.onViewAll && (
                <Button
                  variant="text"
                  onClick={() => {
                    pinnedNotes.onViewAll?.();
                    setNotesDialogOpen(false);
                  }}
                >
                  View All Notes
                </Button>
              )}
            </div>
            <Button variant="filled" onClick={() => setNotesDialogOpen(false)}>
              Close
            </Button>
          </div>
        }
      >
        <div className="overflow-y-auto space-y-4 max-h-[calc(85vh-160px)]">
          {pinnedNotes.notes.map((note) => {
            const noteAction = pinnedNotes.getNoteAction?.(note);
            const priorityType = note.priority === NotePriorityValues.ALERT ? "error" : "information";
            const formattedExpiration = note.priorityExpirationDate
              ? toFormat(note.priorityExpirationDate, "MMM dd, yyyy")
              : null;
            return (
              <DetailsCard
                key={note.id}
                compact
                linked
                options={[
                  {
                    iconName: "sticky_note_2",
                    iconBackground: true,
                    overline: note.displayTitle || note.title || "Untitled Note",
                    headline: note.content,
                    supportingText: note.updatedAt ? toFormat(note.updatedAt, "MMM dd, yyyy") : null,
                    tagText:
                      note.priority && note.priority !== NotePriorityValues.NONE
                        ? formatNotePriority(note.priority)
                        : undefined,
                    tagType: note.priority && note.priority !== NotePriorityValues.NONE ? priorityType : undefined,
                    tagIconName: formattedExpiration ? "schedule" : undefined,
                    tagTooltipText: formattedExpiration ? `Expires ${formattedExpiration}` : undefined,
                    action: noteAction
                      ? () => {
                          noteAction();
                          setNotesDialogOpen(false);
                        }
                      : undefined
                  }
                ]}
              />
            );
          })}
        </div>
      </Dialog>
    );
  };

  // Return the appropriate variant
  const variantContent = (() => {
    switch (effectiveVariant) {
      case "complete":
        return renderCompleteVariant();
      case "condensed":
        return renderCondensedVariant();
      case "landing-page":
        return renderLandingPageVariant();
      default:
        return renderCompleteVariant();
    }
  })();

  return (
    <>
      {variantContent}
      {typeof document !== "undefined" ? createPortal(renderNotesDialog(), document.body) : null}
    </>
  );
}
